/**
 * What the player knows about a recording, ported from avrio-rio
 * (client/react/src/services/workspace/replay/model.ts, main 271ab3a): the
 * lines in time order, and the work space as it was at any moment, rebuilt
 * from the nearest snapshot before it and the changes after. Pure, so a test
 * can check the state at any time against what was recorded.
 */
import { applyChange, changedLines } from './format';
import {
  CANVAS_LABELS,
  LANGUAGE_LABELS,
  type CanvasElement,
  type CanvasKind,
  type ReplayLine,
  type ScreenFrameReason,
  type WorkspaceLanguage,
} from './types';

/** Stretches longer than this with nothing happening are idle, and can be skipped. */
export const IDLE_MS = 10_000;
/** A skipped stretch keeps this much on each side, so what happens next is not cut off. */
const IDLE_LEAD_MS = 1_500;

export type MarkerKind =
  | 'run_pass'
  | 'run_fail'
  | 'assistant'
  | 'constraint'
  | 'tab_switch'
  | 'screen_frame'
  | 'screen_shared'
  | 'screen_stopped'
  | 'done';

export interface ScreenFrame {
  n: number;
  t: number;
  reason: ScreenFrameReason;
}

export interface Marker {
  t: number;
  kind: MarkerKind;
  label: string;
}

export interface Gap {
  from: number;
  to: number;
}

export interface TranscriptItem {
  t: number;
  /** written: a note the candidate typed for the hiring team; note: a line the player adds, such as a language choice. */
  who: 'candidate' | 'assistant' | 'rio' | 'run' | 'voice' | 'written' | 'note';
  text: string;
  ok?: boolean;
}

export interface VoiceSession {
  session: number;
  startT: number;
  /** The audio chunk numbers, in the order they were recorded. */
  chunks: number[];
}

type RunResult = Extract<ReplayLine, { type: 'run_result' }>;

export interface ReplayData {
  title: string;
  kind: 'code' | CanvasKind;
  language: WorkspaceLanguage;
  label: string;
  startedAt: string | null;
  lines: ReplayLine[];
  snapshots: number[];
  canvas: number[];
  durationMs: number;
  markers: Marker[];
  gaps: Gap[];
  transcript: TranscriptItem[];
  voice: VoiceSession[];
  frames: ScreenFrame[];
  /** A change could not be applied: a chunk is missing or damaged. */
  damaged: boolean;
}

export interface ReplayState {
  files: Record<string, string>;
  order: string[];
  editable: Set<string>;
  active: string;
  language: WorkspaceLanguage;
  selection: { anchor: number; head: number } | null;
  lastRun: RunResult | null;
  running: boolean;
  done: boolean;
  damaged: boolean;
}

const ACTIVITY: ReadonlySet<ReplayLine['type']> = new Set<ReplayLine['type']>([
  'change',
  'file_switch',
  'run',
  'run_result',
  'assist_prompt',
  'assist_reply',
  'insert_from_assistant',
  'constraint_shown',
  'rio_message',
  'note',
  'language_chosen',
  'language_changed',
  'done',
  'canvas_snapshot',
]);

const isLanguage = (value: unknown): value is WorkspaceLanguage =>
  typeof value === 'string' && value in LANGUAGE_LABELS;

/** Each NDJSON line as a replay line; a line that is not JSON, or has no type and time, is skipped. */
export function parseLines(text: string): ReplayLine[] {
  const lines: ReplayLine[] = [];
  for (const raw of text.split('\n')) {
    if (!raw.trim()) continue;
    try {
      const line = JSON.parse(raw);
      if (
        line &&
        typeof line.type === 'string' &&
        typeof line.t === 'number' &&
        Number.isFinite(line.t)
      )
        lines.push(line);
    } catch {
      // A damaged line is left out; the next snapshot puts the files right again.
    }
  }
  return lines;
}

const runText = (r: RunResult) =>
  r.error && r.passed + r.failed === 0
    ? 'The tests could not run'
    : `${r.passed} passed${
        r.failed
          ? ` · ${r.failed} failed${
              Array.isArray(r.failedTests) && r.failedTests[0]
                ? `: ${r.failedTests[0]}`
                : ''
            }`
          : ''
      }`;

const labelOf = (language: string) =>
  LANGUAGE_LABELS[language as WorkspaceLanguage] ?? language;

/**
 * Reads a recording: the events chunks' text in chunk order, and the voice
 * transcript when the service has one. Lines are put in time order, keeping
 * the recorded order for lines at the same time.
 */
export function buildReplay(
  eventChunks: string[],
  transcript: { t: number; text: string }[] = [],
): ReplayData {
  const lines = eventChunks
    .flatMap(parseLines)
    .map((line, i) => ({ line, i }))
    .sort((a, b) => a.line.t - b.line.t || a.i - b.i)
    .map((x) => x.line);
  const header =
    lines.find(
      (l): l is Extract<ReplayLine, { type: 'header' }> => l.type === 'header',
    ) ?? null;
  const firstSnapshot = lines.find(
    (l): l is Extract<ReplayLine, { type: 'snapshot' }> =>
      l.type === 'snapshot',
  );
  const kind =
    header?.kind === 'whiteboard' || header?.kind === 'design'
      ? header.kind
      : 'code';
  const headerLanguage =
    kind === 'code' && isLanguage(header?.language)
      ? header?.language
      : undefined;
  let language: WorkspaceLanguage =
    headerLanguage ??
    (isLanguage(firstSnapshot?.language)
      ? firstSnapshot?.language
      : undefined) ??
    'python';
  const snapshots: number[] = [];
  const canvas: number[] = [];
  const markers: Marker[] = [];
  const items: TranscriptItem[] = [];
  const sessions = new Map<number, VoiceSession>();
  const segments: { session: number; seq: number; n: number }[] = [];
  const frames: ScreenFrame[] = [];
  const activity: number[] = [0];
  let end = 0;

  lines.forEach((line, index) => {
    end = Math.max(end, line.t);
    if (
      ACTIVITY.has(line.type) ||
      (line.type === 'snapshot' && line.reason !== 'keyframe')
    )
      activity.push(line.t);
    switch (line.type) {
      case 'snapshot':
        if (!Array.isArray(line.files)) break;
        snapshots.push(index);
        if (isLanguage(line.language)) language = line.language;
        break;
      case 'canvas_snapshot':
        canvas.push(index);
        break;
      case 'run_result': {
        const ok = line.failed === 0 && line.passed > 0 && !line.error;
        markers.push({
          t: line.t,
          kind: ok ? 'run_pass' : 'run_fail',
          label: `Tests: ${runText(line)}`,
        });
        items.push({ t: line.t, who: 'run', text: runText(line), ok });
        break;
      }
      case 'assist_prompt':
        markers.push({
          t: line.t,
          kind: 'assistant',
          label: 'Asked the assistant',
        });
        items.push({ t: line.t, who: 'candidate', text: String(line.text) });
        break;
      case 'assist_reply':
        items.push({ t: line.t, who: 'assistant', text: String(line.text) });
        break;
      case 'insert_from_assistant':
        items.push({
          t: line.t,
          who: 'note',
          text: `Took the assistant's code into ${line.file}`,
        });
        break;
      case 'constraint_shown':
        markers.push({
          t: line.t,
          kind: 'constraint',
          label: 'Rio changed a constraint',
        });
        items.push({ t: line.t, who: 'rio', text: String(line.text) });
        break;
      case 'rio_message':
        items.push({ t: line.t, who: 'rio', text: String(line.text) });
        break;
      case 'note':
        items.push({ t: line.t, who: 'written', text: String(line.text) });
        break;
      case 'language_chosen':
        items.push({
          t: line.t,
          who: 'note',
          text: `Chose ${labelOf(line.language)}`,
        });
        break;
      case 'language_changed':
        items.push({
          t: line.t,
          who: 'note',
          text: `Switched to ${labelOf(line.to)}`,
        });
        break;
      case 'focus_lost':
        markers.push({
          t: line.t,
          kind: 'tab_switch',
          label: 'Left the work space',
        });
        break;
      case 'screen_frame':
        frames.push({ n: line.n, t: line.t, reason: line.reason });
        markers.push({
          t: line.t,
          kind: 'screen_frame',
          label: 'Screen while away',
        });
        break;
      case 'screen_shared':
        markers.push({
          t: line.t,
          kind: 'screen_shared',
          label: 'Screen shared',
        });
        items.push({
          t: line.t,
          who: 'note',
          text: 'Shared their entire screen',
        });
        break;
      case 'screen_share_stopped':
        markers.push({
          t: line.t,
          kind: 'screen_stopped',
          label: 'Stopped sharing',
        });
        items.push({
          t: line.t,
          who: 'note',
          text: 'Stopped sharing their screen; the work space waited',
        });
        break;
      case 'screen_share_resumed':
        markers.push({
          t: line.t,
          kind: 'screen_shared',
          label: 'Screen shared again',
        });
        items.push({
          t: line.t,
          who: 'note',
          text: 'Shared their screen again',
        });
        break;
      case 'done':
        markers.push({ t: line.t, kind: 'done', label: 'Submitted' });
        items.push({
          t: line.t,
          who: 'note',
          text:
            kind !== 'code'
              ? 'Submitted the drawing'
              : line.ran
                ? `Submitted: ${line.passed} passed, ${line.failed} failed`
                : 'Submitted without running the tests',
        });
        break;
      case 'voice_start':
        if (!sessions.has(line.session))
          sessions.set(line.session, {
            session: line.session,
            startT: line.t,
            chunks: [],
          });
        break;
      case 'audio_segment':
        segments.push({ session: line.session, seq: line.seq, n: line.n });
        break;
      case 'end':
        if (
          typeof line.durationMs === 'number' &&
          Number.isFinite(line.durationMs)
        )
          end = Math.max(end, line.durationMs);
        break;
      default:
        break;
    }
  });

  for (const segment of segments.sort((a, b) => a.seq - b.seq))
    sessions.get(segment.session)?.chunks.push(segment.n);
  for (const line of transcript) {
    if (
      typeof line?.t === 'number' &&
      typeof line?.text === 'string' &&
      line.text.trim()
    ) {
      items.push({ t: line.t, who: 'voice', text: line.text });
    }
  }
  items.sort((a, b) => a.t - b.t);

  activity.push(end);
  activity.sort((a, b) => a - b);
  const gaps: Gap[] = [];
  for (let i = 1; i < activity.length; i += 1) {
    if (activity[i] - activity[i - 1] > IDLE_MS) {
      gaps.push({
        from: activity[i - 1] + IDLE_LEAD_MS,
        to: activity[i] - IDLE_LEAD_MS,
      });
    }
  }

  const replay: ReplayData = {
    title: typeof header?.title === 'string' ? header.title : '',
    kind,
    language: headerLanguage ?? language,
    label:
      (typeof header?.label === 'string' && header.label) ||
      (kind === 'code' ? LANGUAGE_LABELS[language] : CANVAS_LABELS[kind]),
    startedAt: typeof header?.startedAt === 'string' ? header.startedAt : null,
    lines,
    snapshots,
    canvas,
    durationMs: end,
    markers,
    gaps,
    transcript: items,
    voice: Array.from(sessions.values())
      .filter((s) => s.chunks.length > 0)
      .sort((a, b) => a.startT - b.startT),
    frames: frames.sort((a, b) => a.t - b.t || a.n - b.n),
    damaged: false,
  };
  // Replaying it once, start to end, finds any change that does not fit, so the player can say a part is missing.
  replay.damaged = findsDamage(lines);
  return replay;
}

const filesOf = (line: Extract<ReplayLine, { type: 'snapshot' }>) =>
  Object.fromEntries(
    line.files
      .filter((f) => f && typeof f.name === 'string')
      .map((f) => [f.name, typeof f.content === 'string' ? f.content : '']),
  );

function findsDamage(lines: ReplayLine[]): boolean {
  let files: Record<string, string> = {};
  for (const line of lines) {
    if (line.type === 'snapshot' && Array.isArray(line.files))
      files = filesOf(line);
    if (line.type !== 'change' || !(line.file in files)) continue;
    try {
      files[line.file] = applyChange(files[line.file], line.changes);
    } catch {
      return true;
    }
  }
  return false;
}

/** The index of the last entry of `indexes` whose line is at or before `t`, or `fallback`. Binary search. */
function lastAtOrBefore(
  lines: ReplayLine[],
  indexes: number[],
  t: number,
  fallback: number,
): number {
  let lo = 0;
  let hi = indexes.length - 1;
  let found = fallback;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (lines[indexes[mid]].t <= t) {
      found = indexes[mid];
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
}

/**
 * The work space at time `t`: from the nearest snapshot before it, then every
 * line up to `t`. `damaged` says a change did not fit the text it was
 * recorded against.
 */
export function stateAt(replay: ReplayData, t: number): ReplayState {
  // Before the first snapshot (a recording stamped a moment after 0), the work space is as that snapshot shows it.
  const from = lastAtOrBefore(
    replay.lines,
    replay.snapshots,
    t,
    replay.snapshots[0] ?? -1,
  );
  const state: ReplayState = {
    files: {},
    order: [],
    editable: new Set(),
    active: '',
    language: replay.language,
    selection: null,
    lastRun: null,
    running: false,
    done: false,
    damaged: false,
  };
  if (from >= 0) {
    const snapshot = replay.lines[from] as Extract<
      ReplayLine,
      { type: 'snapshot' }
    >;
    for (const file of snapshot.files) {
      if (!file || typeof file.name !== 'string') continue;
      state.files[file.name] =
        typeof file.content === 'string' ? file.content : '';
      state.order.push(file.name);
      if (file.editable) state.editable.add(file.name);
    }
    state.active = snapshot.active;
    if (isLanguage(snapshot.language)) state.language = snapshot.language;
  }
  // Runs and Done are read from the start: a snapshot does not carry them.
  for (const line of replay.lines) {
    if (line.t > t) break;
    if (line.type === 'run') state.running = true;
    if (line.type === 'run_result') {
      state.running = false;
      state.lastRun = line;
    }
    if (line.type === 'done') state.done = true;
  }
  for (let i = from + 1; i < replay.lines.length; i += 1) {
    const line = replay.lines[i];
    if (line.t > t) break;
    if (line.type === 'change' && line.file in state.files) {
      try {
        state.files[line.file] = applyChange(
          state.files[line.file],
          line.changes,
        );
      } catch {
        state.damaged = true;
      }
    } else if (line.type === 'file_switch') {
      state.active = line.file;
      state.selection = null;
    } else if (line.type === 'selection' && line.file === state.active) {
      const length = (state.files[line.file] ?? '').length;
      state.selection = {
        anchor: Math.max(0, Math.min(line.anchor, length)),
        head: Math.max(0, Math.min(line.head, length)),
      };
    }
  }
  if (!state.active || !(state.active in state.files))
    state.active = state.order[0] ?? '';
  return state;
}

/** A canvas task's drawing at `t`: the nearest canvas snapshot at or before it, or the first one before any. */
export function canvasAt(replay: ReplayData, t: number): CanvasElement[] {
  const found = lastAtOrBefore(
    replay.lines,
    replay.canvas,
    t,
    replay.canvas[0] ?? -1,
  );
  const line = found >= 0 ? replay.lines[found] : null;
  return line?.type === 'canvas_snapshot' && Array.isArray(line.elements)
    ? line.elements
    : [];
}

/** Where playback goes from `t` when idle stretches are skipped: the end of the stretch it is in, or `t`. */
export function skipIdle(replay: ReplayData, t: number): number {
  const gap = replay.gaps.find((g) => t >= g.from && t < g.to);
  return gap ? gap.to : t;
}

/** How many transcript items have happened by `t`. */
export function transcriptCount(replay: ReplayData, t: number): number {
  let lo = 0;
  let hi = replay.transcript.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (replay.transcript[mid].t <= t) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

const isLinear = (type: string) => type === 'arrow' || type === 'line';

/** One element as a string of its contract fields: two that say the same thing compare equal. */
const keyOf = (e: CanvasElement) =>
  [
    e.id,
    e.type,
    e.x,
    e.y,
    e.w,
    e.h,
    e.text ?? '',
    e.label ?? '',
    e.startId ?? '',
    e.endId ?? '',
  ].join('\u0001');

/** What changed between two drawings, by id: parts added, changed and removed. */
export function diffElements(
  before: readonly CanvasElement[],
  after: readonly CanvasElement[],
): { added: number; changed: number; removed: number } {
  const old = new Map(before.map((e) => [e.id, keyOf(e)]));
  const now = new Set(after.map((e) => e.id));
  let added = 0;
  let changed = 0;
  for (const e of after) {
    const was = old.get(e.id);
    if (was === undefined) added += 1;
    else if (was !== keyOf(e)) changed += 1;
  }
  const removed = before.filter((e) => !now.has(e.id)).length;
  return { added, changed, removed };
}

/** Parts (a non-arrow element with words) and connections (an arrow or line joining two parts). */
export function drawingCounts(elements: readonly CanvasElement[]): {
  parts: number;
  connections: number;
} {
  const named = new Set(
    elements.filter((e) => !isLinear(e.type) && e.text).map((e) => e.id),
  );
  return {
    parts: named.size,
    connections: elements.filter(
      (e) =>
        isLinear(e.type) &&
        e.startId &&
        e.endId &&
        named.has(e.startId) &&
        named.has(e.endId),
    ).length,
  };
}

/** What the window after a return is read over, for its one-line summary. */
export const AFTER_RETURN_MS = 60_000;

export interface FileView {
  file: string;
  content: string;
  /** The 1-based line to centre a small view on: the first line that differs, or the cursor. */
  focusLine: number;
}

export interface TabSwitch {
  leftAt: number;
  returnedAt: number | null;
  awayMs: number | null;
  before: FileView;
  after: FileView | null;
  summary: string;
  frames: (ScreenFrame & { offsetMs: number })[];
  canvas?: { before: CanvasElement[]; after: CanvasElement[] | null };
}

/** The frame taken as they come back lands a moment after the return: it still belongs to that absence. */
const RETURN_FRAME_SLACK_MS = 15_000;

const lineOf = (content: string, offset: number) =>
  content.slice(0, Math.max(0, offset)).split('\n').length;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const NO_FILE: FileView = { file: '', content: '', focusLine: 1 };

/**
 * Every time the candidate left the work space (another tab, window or app)
 * and came back: when, for how long, the open file at both moments, and what
 * changed in the minute after they returned.
 */
export function tabSwitches(replay: ReplayData): TabSwitch[] {
  const switches: TabSwitch[] = [];
  const lines = replay.lines;
  const leaves = lines.filter((l) => l.type === 'focus_lost').map((l) => l.t);
  for (let i = 0; i < lines.length; i += 1) {
    const lost = lines[i];
    if (lost.type !== 'focus_lost') continue;
    const back = lines
      .slice(i + 1)
      .find(
        (l): l is Extract<ReplayLine, { type: 'focus_returned' }> =>
          l.type === 'focus_returned',
      );
    const nextLeave = leaves.find((t) => t > lost.t) ?? Infinity;
    const framesUntil = back
      ? Math.min(nextLeave, back.t + RETURN_FRAME_SLACK_MS)
      : nextLeave;
    const frames = replay.frames
      .filter((f) => f.t >= lost.t && f.t <= framesUntil && f.t < nextLeave)
      .map((f) => ({ ...f, offsetMs: f.t - lost.t }));
    if (replay.kind !== 'code') {
      switches.push(canvasSwitch(replay, lost.t, back ?? null, frames));
      continue;
    }
    const left = stateAt(replay, lost.t);
    const beforeContent = left.files[left.active] ?? '';
    const before: FileView = {
      file: left.active,
      content: beforeContent,
      focusLine: left.selection
        ? lineOf(beforeContent, left.selection.head)
        : 1,
    };
    if (!back) {
      switches.push({
        leftAt: lost.t,
        returnedAt: null,
        awayMs: null,
        before,
        after: null,
        summary: "Didn't come back before the recording ended",
        frames,
      });
      continue;
    }
    const returned = stateAt(replay, back.t);
    const afterContent = returned.files[returned.active] ?? '';
    const differs =
      returned.active === left.active
        ? changedLines(beforeContent, afterContent)
        : null;
    const after: FileView = {
      file: returned.active,
      content: afterContent,
      focusLine:
        differs?.from ??
        (returned.selection
          ? lineOf(afterContent, returned.selection.head)
          : before.focusLine),
    };
    if (differs) before.focusLine = differs.from;

    const until = Math.min(back.t + AFTER_RETURN_MS, replay.durationMs);
    const later = stateAt(replay, until);
    const inWindow = lines.filter((l) => l.t >= back.t && l.t <= until);
    const changes = Object.keys(later.files)
      .map((file) => ({
        file,
        diff: changedLines(returned.files[file] ?? '', later.files[file]),
      }))
      .filter(
        (
          c,
        ): c is {
          file: string;
          diff: NonNullable<ReturnType<typeof changedLines>>;
        } => c.diff !== null,
      );
    const paste = inWindow.find(
      (l): l is Extract<ReplayLine, { type: 'paste_external' }> =>
        l.type === 'paste_external',
    );
    const insert = inWindow.find(
      (l): l is Extract<ReplayLine, { type: 'insert_from_assistant' }> =>
        l.type === 'insert_from_assistant',
    );
    let summary = 'No change';
    if (paste) {
      const added =
        changes.find((c) => c.file === paste.file)?.diff.added ?? paste.chars;
      summary = `Pasted ${plural(Math.max(1, added), 'line')} into ${paste.file}`;
    } else if (insert) {
      summary = `Took the assistant's code into ${insert.file}`;
    } else if (changes.length) {
      const [first, ...rest] = changes;
      summary = `Edited ${plural(
        Math.max(first.diff.added, first.diff.removed),
        'line',
      )} in ${first.file}${
        rest.length ? ` and ${plural(rest.length, 'other file')}` : ''
      }`;
    }
    switches.push({
      leftAt: lost.t,
      returnedAt: back.t,
      awayMs: back.awayMs,
      before,
      after,
      summary,
      frames,
    });
  }
  return switches;
}

/** A tab switch on a canvas task: the drawing at both moments, and what changed in it in the minute after the return. */
function canvasSwitch(
  replay: ReplayData,
  leftAt: number,
  back: Extract<ReplayLine, { type: 'focus_returned' }> | null,
  frames: TabSwitch['frames'],
): TabSwitch {
  const before = canvasAt(replay, leftAt);
  if (!back) {
    return {
      leftAt,
      returnedAt: null,
      awayMs: null,
      before: NO_FILE,
      after: null,
      summary: "Didn't come back before the recording ended",
      frames,
      canvas: { before, after: null },
    };
  }
  const after = canvasAt(replay, back.t);
  const later = canvasAt(
    replay,
    Math.min(back.t + AFTER_RETURN_MS, replay.durationMs),
  );
  const { added, changed, removed } = diffElements(after, later);
  const parts = [
    added && `added ${plural(added, 'element')}`,
    changed && `changed ${plural(changed, 'element')}`,
    removed && `removed ${plural(removed, 'element')}`,
  ].filter(Boolean) as string[];
  const text = parts.join(', ');
  return {
    leftAt,
    returnedAt: back.t,
    awayMs: back.awayMs,
    before: NO_FILE,
    after: NO_FILE,
    summary: text
      ? `${text[0].toUpperCase()}${text.slice(1)} in the drawing`
      : 'No change',
    frames,
    canvas: { before, after },
  };
}
